import { colors } from '@lmc/common/constants'
import { AlertIcon, CheckCircleIcon, LineChart } from '@lmc/design-system'
import { cssVars } from '@lmc/design-system/tokens'
import dayjs from 'dayjs'
import { NAMESPACE } from 'locale'
import { emarV2Constants } from 'locale/emarV2'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

const { color, fontWeight, radius, spacing, text } = cssVars

/** One plotted stock level, with the fields its tooltip shows. */
export type MedStockPoint = {
  counted?: number
  /** The count differs from the expected level; a plain dot is drawn in the alert colour. */
  countMismatch?: boolean
  discrepancy?: { reason: string; resolved: boolean }
  expected?: number
  /** Icon drawn on the point; without one it gets a plain dot. */
  marker?: 'alert' | 'tick'
  notes: string
  recordedBy: string
  stock: number
  /** ms */
  time: number
  title: string
}

type MedStockLineChartProps = {
  endDate: dayjs.Dayjs
  points: MedStockPoint[]
  startDate: dayjs.Dayjs
  stockQuantityType?: string
  testId?: string
}

type DotProps = {
  cx?: number
  cy?: number
  index?: number
  payload?: MedStockPoint
}

const LINE_COLOUR = colors.blue
const MARKER_COLOURS = {
  alert: colors.yellow,
  tick: colors.blue
}
const ICON_SIZE = 16

const Card = styled.div`
  padding: ${spacing['300']} ${spacing['200']} 0 0;
  border-radius: ${radius['200']};
  background: ${color.bg.primary};
`

const Heading = styled.span`
  color: ${color.text.secondary};
`

const Title = styled.span`
  font-size: ${text.body.sm.size};
  font-weight: ${fontWeight.semibold};
`

const Detail = styled.span`
  color: ${color.text.secondary};
`

const Discrepancy = styled.span<{ $resolved: boolean }>`
  font-weight: ${fontWeight.semibold};
  color: ${({ $resolved }) =>
    $resolved ? color.text.status.success : color.text.status.warning};
`

const renderDot = ({ cx = 0, cy = 0, index, payload }: DotProps) => {
  const marker = payload?.marker
  if (!marker) {
    return (
      <circle
        key={index}
        cx={cx}
        cy={cy}
        fill={color.bg.primary}
        r={4}
        stroke={payload?.countMismatch ? MARKER_COLOURS.alert : LINE_COLOUR}
        strokeWidth={2}
      />
    )
  }
  const Icon = marker === 'alert' ? AlertIcon : CheckCircleIcon
  return (
    <svg
      key={index}
      color={MARKER_COLOURS[marker]}
      aria-hidden="true"
      data-marker={marker}
      height={ICON_SIZE}
      width={ICON_SIZE}
      x={cx - ICON_SIZE / 2}
      y={cy - ICON_SIZE / 2}
    >
      <Icon size={ICON_SIZE} />
    </svg>
  )
}

/** Stock level over time for one medication, shared by the eMAR V1 and V2 stock screens. */
const MedStockLineChart = ({
  endDate,
  points,
  startDate,
  stockQuantityType = '',
  testId
}: MedStockLineChartProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const yAxisTitle = stockQuantityType
    ? t(emarV2Constants.STOCK_COUNT_UNIT, { unit: stockQuantityType })
    : t(emarV2Constants.STOCK_COUNT)
  return (
    <Card
      data-testid={testId ? `${testId}-med-stock-chart` : 'med-stock-chart'}
    >
      <LineChart
        aria-label={yAxisTitle}
        data={points}
        emptyMessage={t(emarV2Constants.STOCK_CHART_NO_DATA)}
        height={400}
        series={[
          {
            colour: LINE_COLOUR,
            curve: 'linear',
            dot: renderDot,
            key: 'stock',
            label: t(emarV2Constants.STOCK)
          }
        ]}
        tooltip={({ row }) => {
          const point = row as MedStockPoint
          return (
            <>
              <Heading>
                {dayjs(point.time).format('DD-MM-YYYY HH:mm')} -{' '}
                {point.recordedBy}
              </Heading>
              <Title>{point.title}</Title>
              {point.notes ? <span>{point.notes}</span> : null}
              {point.counted !== undefined ? (
                <Detail>
                  {t(emarV2Constants.STOCK_CHART_COUNTED, {
                    counted: point.counted,
                    unit: stockQuantityType
                  })}
                </Detail>
              ) : null}
              {point.expected !== undefined ? (
                <Detail>
                  {t(emarV2Constants.STOCK_CHART_EXPECTED, {
                    expected: point.expected,
                    unit: stockQuantityType
                  })}
                </Detail>
              ) : null}
              {point.discrepancy ? (
                <Discrepancy $resolved={point.discrepancy.resolved}>
                  {t(
                    point.discrepancy.resolved
                      ? emarV2Constants.STOCK_CHART_RESOLVED
                      : emarV2Constants.STOCK_CHART_DISCREPANCY,
                    { reason: point.discrepancy.reason }
                  )}
                </Discrepancy>
              ) : null}
            </>
          )
        }}
        xAxis={{
          domain: [
            startDate.startOf('day').valueOf(),
            endDate.endOf('day').valueOf()
          ],
          title: t(emarV2Constants.DATE)
        }}
        xKey="time"
        yAxis={{ allowDecimals: true, title: yAxisTitle }}
      />
    </Card>
  )
}

export default MedStockLineChart
